const STORE_TIME_ZONE = 'America/New_York';
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;

class StoreHoursCountdown extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  timerId;

  /** @type {Intl.DateTimeFormat | undefined} */
  timeFormatter;

  connectedCallback() {
    if (this.timerId !== undefined) clearTimeout(this.timerId);
    this.updateCountdown();
    this.scheduleNextUpdate();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  disconnectedCallback() {
    if (this.timerId !== undefined) clearTimeout(this.timerId);
    this.timerId = undefined;
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.updateCountdown();
  };

  scheduleNextUpdate() {
    const now = new Date();
    const millisecondsUntilNextMinute =
      (SECONDS_PER_MINUTE - now.getSeconds()) * 1000 - now.getMilliseconds() + 50;

    this.timerId = setTimeout(() => {
      this.updateCountdown();
      this.scheduleNextUpdate();
    }, millisecondsUntilNextMinute);
  }

  updateCountdown() {
    const messageElement = this.querySelector('[data-store-hours-message]');
    if (!(messageElement instanceof HTMLElement)) return;

    try {
      const openHour = Number.parseInt(this.dataset.openHour ?? '', 10);
      const closeHour = Number.parseInt(this.dataset.closeHour ?? '', 10);

      if (!this.hasValidHours(openHour, closeHour)) throw new Error('Invalid store hours');

      const { hour, minute, second } = this.getStoreTimeParts(new Date());
      const currentSeconds = hour * SECONDS_PER_HOUR + minute * SECONDS_PER_MINUTE + second;
      const openSeconds = openHour * SECONDS_PER_HOUR;
      const closeSeconds = closeHour * SECONDS_PER_HOUR;
      const isOpen = currentSeconds >= openSeconds && currentSeconds < closeSeconds;
      const remainingSeconds = isOpen
        ? closeSeconds - currentSeconds
        : this.getSecondsUntilOpening(currentSeconds, openSeconds);
      const remainingMinutes = Math.ceil(remainingSeconds / SECONDS_PER_MINUTE);
      const timeText = this.formatDuration(remainingMinutes);
      const template = isOpen ? this.dataset.openTemplate : this.dataset.closedTemplate;
      const message = this.fillTemplate(template, timeText, this.formatHour(openHour));

      messageElement.textContent = message;
      messageElement.setAttribute('aria-label', message);
      this.dataset.storeState = isOpen ? 'open' : 'closed';
    } catch (error) {
      const fallback = this.dataset.fallback ?? '';
      messageElement.textContent = fallback;
      messageElement.setAttribute('aria-label', fallback);
      this.dataset.storeState = 'unavailable';
    }
  }

  /**
   * @param {number} openHour
   * @param {number} closeHour
   * @returns {boolean}
   */
  hasValidHours(openHour, closeHour) {
    return (
      Number.isInteger(openHour) &&
      Number.isInteger(closeHour) &&
      openHour >= 0 &&
      openHour <= 23 &&
      closeHour >= 0 &&
      closeHour <= 23 &&
      openHour < closeHour
    );
  }

  /**
   * @param {Date} date
   * @returns {{ hour: number; minute: number; second: number }}
   */
  getStoreTimeParts(date) {
    this.timeFormatter ??= new Intl.DateTimeFormat('en-US', {
      timeZone: this.dataset.timeZone || STORE_TIME_ZONE,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });

    const parts = Object.fromEntries(
      this.timeFormatter
        .formatToParts(date)
        .filter(({ type }) => type === 'hour' || type === 'minute' || type === 'second')
        .map(({ type, value }) => [type, Number.parseInt(value, 10)])
    );

    if (![parts.hour, parts.minute, parts.second].every(Number.isInteger)) {
      throw new Error('Unable to read store time');
    }

    return {
      hour: parts.hour === 24 ? 0 : parts.hour,
      minute: parts.minute,
      second: parts.second,
    };
  }

  /**
   * @param {number} currentSeconds
   * @param {number} openSeconds
   * @returns {number}
   */
  getSecondsUntilOpening(currentSeconds, openSeconds) {
    if (currentSeconds < openSeconds) return openSeconds - currentSeconds;
    return SECONDS_PER_DAY - currentSeconds + openSeconds;
  }

  /**
   * @param {number} totalMinutes
   * @returns {string}
   */
  formatDuration(totalMinutes) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const parts = [];

    if (hours > 0) {
      parts.push(`${hours} ${hours === 1 ? this.dataset.hourOne : this.dataset.hourOther}`);
    }

    if (minutes > 0) {
      parts.push(`${minutes} ${minutes === 1 ? this.dataset.minuteOne : this.dataset.minuteOther}`);
    }

    return parts.join(' ');
  }

  /**
   * @param {number} hour
   * @returns {string}
   */
  formatHour(hour) {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour} ${period}`;
  }

  /**
   * @param {string | undefined} template
   * @param {string} timeText
   * @param {string} openTimeText
   * @returns {string}
   */
  fillTemplate(template, timeText, openTimeText) {
    const fallback = this.dataset.fallback ?? '';
    if (!template) return fallback;

    return template.split('[time]').join(timeText).split('[open_time]').join(openTimeText);
  }
}

if (!customElements.get('store-hours-countdown')) {
  customElements.define('store-hours-countdown', StoreHoursCountdown);
}
